/** @file Sends commands as one undo step through the editor dispatch; the result a form shows. */
import { makeError } from '@pose2d/format/pose2dError';
import { toPose2DError } from '@pose2d/format/toPose2DError';
import type { CommandCall, Id, Pose2DError, Warning } from '@pose2d/format/types';
import { dispatch } from '../api/sync/dispatch';

/** The refusal (or null), the warnings of one submitted batch and, after a success, the ids its commands created by name. */
export interface SubmitResult {
  error: Pose2DError | null;
  warnings: readonly Warning[];
  /** `dispatch(...).created`: entity name → id minted by the optimistic apply (W3-11). */
  created?: Readonly<Record<string, Id>>;
}

/** A Pose2DError carried by a thrown Error, the bare value (as the REST client rejects), or INTERNAL for anything else. */
function asError(thrown: unknown): Pose2DError {
  const known = toPose2DError(thrown) ?? toPose2DError({ pose2d: thrown });
  return known ?? makeError('INTERNAL', undefined, { hint: thrown instanceof Error ? thrown.message : String(thrown) });
}

/** One dispatch for all `calls` (one commit); an empty list is a no-op success. A local refusal and a server refusal both come back as `error`. */
export async function submitCalls(calls: readonly CommandCall[]): Promise<SubmitResult> {
  if (calls.length === 0) return { error: null, warnings: [] };
  try {
    const handle = dispatch([...calls]);
    const answer = await handle.done;
    return { error: null, warnings: answer.warnings, created: handle.created };
  } catch (thrown) {
    return { error: asError(thrown), warnings: [] };
  }
}
